import axios from 'axios'
import { apiConfig, API_BASE_URL, CMS_API_BASE_URL, SHOP_API_BASE_URL } from '@/config/api'
import router from '@/router'

/**
 * Axios HTTP Client
 * 
 * CMS routes use: https://inversuncms.on-forge.com/api
 * Shop routes use: https://oliverlaravel.on-forge.com/api (via interceptor)
 */
const apiClient = axios.create({
  // Endpoints from `apiEndpoints` are already fully formed relative/absolute URLs.
  // Leaving baseURL empty avoids `/cms-api/cms-api/...` duplication in development.
  baseURL: '',
  headers: apiConfig.headers,
  timeout: apiConfig.timeout,
  withCredentials: apiConfig.withCredentials,
})

// Token management
const TOKEN_KEY = 'inversun-auth-token'

export const getAuthToken = () => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(TOKEN_KEY)
}

export const setAuthToken = (token) => {
  if (typeof window === 'undefined') return
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

export const removeAuthToken = () => {
  setAuthToken(null)
}

// Helper to check if current route is a shop route (uses Oliver Laravel API)
const isShopRoute = () => {
  if (!router.currentRoute) return false
  const routeName = router.currentRoute.value?.name
  // Shop routes use Oliver Laravel API
  const shopRoutes = [
    'shop',
    'shop-category',
    'product',
    'cart',
    'checkout',
    'account',
    'orders',
    'order-detail',
    'wishlist'
  ]
  return shopRoutes.includes(routeName)
}

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Dynamically swap the base URL for shop routes
    // Since endpoints are absolute URLs, we must replace the prefix directly
    // (axios ignores baseURL when config.url is already absolute)
    if (isShopRoute() && config.url && config.url.startsWith(CMS_API_BASE_URL)) {
      config.url = config.url.replace(CMS_API_BASE_URL, SHOP_API_BASE_URL)
    }
    
    // Add auth token if available
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`, config.params || '')
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.url}`, response.status)
    }
    return response
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      const { status } = error.response
      
      switch (status) {
        case 401:
          console.error('Unauthorized - Please log in')
          // Redirect to login or refresh token
          break
        case 403:
          console.error('Forbidden - You don\'t have permission')
          break
        case 404:
          console.error('Resource not found')
          break
        case 422:
          console.error('Validation error:', error.response.data.errors)
          break
        case 500:
          console.error('Server error - Please try again later')
          break
        default:
          console.error(`API Error ${status}:`, error.message)
      }
    } else if (error.request) {
      console.error('Network error - Please check your connection')
    }
    
    return Promise.reject(error)
  }
)

export default apiClient

// Also export the config for use in other modules
export { API_BASE_URL, CMS_API_BASE_URL, SHOP_API_BASE_URL }
